import {
  campaignAnalyticsGranularitySchema,
  campaignAnalyticsKpiKeySchema,
  isPrivilegedRole,
} from "@adpilot/shared";
import { IconDatabaseOff } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { type CSSProperties, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CampaignCreateMenu } from "@/components/campaign/campaign-list/CampaignCreateMenu";
import {
  CampaignFrame,
  CampaignHeading,
} from "@/components/campaign/campaign-list/CampaignListFrame";
import { CampaignListToolbar } from "@/components/campaign/campaign-list/CampaignListToolbar";
import { CampaignSmartCreationView } from "@/components/campaign/campaign-list/CampaignSmartCreationView";
import { CampaignTableHeader } from "@/components/campaign/campaign-list/CampaignTableHeader";
import { CampaignRow } from "@/components/campaign/campaign-list/CampaignTableRow";
import { useCampaignList } from "@/components/campaign/campaign-list/use-campaign-list";
import { useCampaignSmartCreation } from "@/components/campaign/campaign-list/use-campaign-smart-creation";
import { CampaignPerformancePanel } from "@/components/campaign/performance/CampaignPerformancePanel";
import { DataGridBody, DataTableFooter } from "@/components/patterns/DataTable";
import { DataTablePagination } from "@/components/patterns/DataTablePagination";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useCampaignTableColumnWidths } from "@/hooks/use-campaign-table-column-widths";
import { useCampaignTableColumns } from "@/hooks/use-campaign-table-columns";
import { useShopScope } from "@/hooks/use-shop-scope";
import { asinFilterSearchSchema } from "@/lib/asin-filter-search";
import { requireModule } from "@/lib/module-access";
import { useAuthStore } from "@/stores/auth";

export const Route = createFileRoute("/_authed/campaigns/")({
  beforeLoad: () => {
    requireModule("campaigns");
  },
  validateSearch: z.object({
    metric: campaignAnalyticsKpiKeySchema.catch("spend"),
    granularity: campaignAnalyticsGranularitySchema.catch("day"),
    delivery: z.enum(["all", "enabled", "paused"]).catch("all"),
    page: z.coerce.number().int().positive().catch(1),
    pageSize: z.coerce
      .number()
      .pipe(z.union([z.literal(10), z.literal(20), z.literal(50)]))
      .catch(20),
    smartCreation: z.string().uuid().optional().catch(undefined),
    creationTask: z.string().uuid().optional().catch(undefined),
    // ASIN filter carried over from the dashboard ("View all" on Top campaigns).
    asins: asinFilterSearchSchema.optional().catch(undefined),
  }),
  component: CampaignsPage,
});

function CampaignsPage() {
  const { t } = useTranslation("campaign");
  const analyticsSearch = Route.useSearch();
  const navigate = Route.useNavigate();
  const shops = useAuthStore((state) => state.shops);
  const user = useAuthStore((state) => state.user);
  const activeWorkspaceId = user?.activeWorkspaceId;
  const role = user?.workspaces?.find((workspace) => workspace.id === activeWorkspaceId)?.role;
  const canCreate = isPrivilegedRole(role);
  const pageSize = analyticsSearch.pageSize;
  const columnPreferences = useCampaignTableColumns();
  const columnSizing = useCampaignTableColumnWidths(columnPreferences.visibleColumnIds);
  const campaignGridStyle = useMemo<CSSProperties>(
    () => ({ gridTemplateColumns: columnSizing.gridTemplateColumns }),
    [columnSizing.gridTemplateColumns],
  );
  // ADP-110: the analytics scope (multi-select, default all workspace shops);
  // the sorted set keys both queries so a scope change refetches.
  const { scopeShopIds } = useShopScope();
  const smartCreation = useCampaignSmartCreation(scopeShopIds);
  const list = useCampaignList({ scopeShopIds, smartCreationOpen: smartCreation.open });
  const { query, analyticsQuery, currentPage, pageCount, pageItems } = list;

  const createMenu = canCreate ? (
    <CampaignCreateMenu
      smartCreationPending={smartCreation.createMutation.isPending}
      smartCreationDisabled={smartCreation.createMutation.isPending || !smartCreation.shopId}
      onCreateManual={() => void navigate({ to: "/campaigns/new" })}
      onCreateSmart={() => smartCreation.createMutation.mutate()}
    />
  ) : null;
  const performancePanel = (
    <CampaignPerformancePanel
      data={analyticsQuery.data}
      isLoading={analyticsQuery.isLoading}
      isFetching={analyticsQuery.isFetching || list.analyticsSearchTerm !== list.search}
      isError={analyticsQuery.isError}
      metric={analyticsSearch.metric}
      granularity={analyticsSearch.granularity}
      onMetricChange={(metric) =>
        void navigate({ search: (previous) => ({ ...previous, metric }), replace: true })
      }
      onGranularityChange={(granularity) =>
        void navigate({ search: (previous) => ({ ...previous, granularity }), replace: true })
      }
      onRetry={() => void analyticsQuery.refetch()}
    />
  );
  const acceptedTaskId = smartCreation.acceptedTaskId;
  useEffect(() => {
    if (!acceptedTaskId || analyticsSearch.creationTask) return;
    void navigate({
      search: (previous) => ({
        ...previous,
        smartCreation: undefined,
        creationTask: acceptedTaskId,
      }),
      replace: true,
    });
  }, [acceptedTaskId, analyticsSearch.creationTask, navigate]);
  if (smartCreation.open) {
    return (
      <CampaignSmartCreationView
        smartCreation={smartCreation}
        canEdit={canCreate}
        shops={shops}
        activeWorkspaceId={activeWorkspaceId}
      />
    );
  }
  if (query.isLoading) {
    return (
      <CampaignFrame>
        <CampaignHeading />
        {performancePanel}
        <Skeleton className="h-[520px] rounded-[10px]" />
      </CampaignFrame>
    );
  }
  if (query.isError || !query.data) {
    return (
      <CampaignFrame>
        <CampaignHeading />
        {performancePanel}
        <ErrorState message={t("list.loadFailed")} onRetry={() => void query.refetch()} />
      </CampaignFrame>
    );
  }
  // With an ASIN filter an empty list is a filter result, not "no campaigns yet": keep the
  // toolbar so the filter can be cleared.
  if (query.data.campaigns.length === 0 && list.asins.length === 0) {
    return (
      <CampaignFrame>
        <CampaignHeading />
        {performancePanel}
        <EmptyState
          icon={<IconDatabaseOff className="size-16" />}
          title={t("list.empty.title")}
          description={t("list.empty.description")}
          actions={createMenu}
        />
      </CampaignFrame>
    );
  }

  return (
    <CampaignFrame>
      <CampaignHeading />
      {performancePanel}
      <section className="flex min-h-[520px] flex-none flex-col overflow-hidden rounded-[14px] border border-line bg-surface">
        <CampaignListToolbar
          list={list}
          campaigns={query.data.campaigns}
          createMenu={createMenu}
          columnPreferences={columnPreferences}
          onDeliveryChange={(value) => {
            void navigate({
              search: (previous) => ({ ...previous, delivery: value, page: 1 }),
              replace: true,
            });
          }}
        />
        <section
          aria-label={t("list.table.scrollRegionAria")}
          className="min-w-0 overflow-x-auto paper-scrollbar"
        >
          <div className="min-w-full" style={{ width: `max(100%, ${columnSizing.tableWidth}px)` }}>
            <CampaignTableHeader
              columnWidths={columnSizing.widths}
              gridStyle={campaignGridStyle}
              onResizeColumn={columnSizing.resizeColumn}
              onResetColumn={columnSizing.resetColumn}
              sortDirection={list.sortDirection}
              sortKey={list.sortKey}
              onSort={list.handleSort}
              visibleColumnIds={columnPreferences.visibleColumnIds}
            />
            <DataGridBody>
              {pageItems.map((campaign) => (
                <CampaignRow
                  key={campaign.id}
                  campaign={campaign}
                  canWrite={canCreate}
                  currencyCode={query.data.currency ?? "USD"}
                  gridStyle={campaignGridStyle}
                  switchWidth={columnSizing.widths.switch}
                  visibleColumnIds={columnPreferences.visibleColumnIds}
                />
              ))}
            </DataGridBody>
          </div>
        </section>
        {pageItems.length === 0 ? (
          <div className="flex min-h-40 flex-1 items-center justify-center text-sm text-muted-foreground">
            {t("list.noMatch")}
          </div>
        ) : null}
        <DataTableFooter className="mt-auto">
          <DataTablePagination
            page={currentPage}
            pageCount={pageCount}
            pageSize={pageSize}
            summary={t("list.pagination", {
              from: (currentPage - 1) * pageSize + (pageItems.length ? 1 : 0),
              to: (currentPage - 1) * pageSize + pageItems.length,
              total: list.filteredCount,
            })}
            onPageChange={(nextPage) =>
              void navigate({
                search: (previous) => ({ ...previous, page: nextPage }),
                replace: true,
              })
            }
            onPageSizeChange={(nextPageSize) => {
              void navigate({
                search: (previous) => ({ ...previous, page: 1, pageSize: nextPageSize }),
                replace: true,
              });
            }}
          />
        </DataTableFooter>
      </section>
    </CampaignFrame>
  );
}
